import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiMessageSquare, FiLayout, FiCode, FiCheckCircle,
  FiSmartphone, FiGlobe, FiShield, FiTrendingUp,
  FiFileText, FiDatabase, FiUploadCloud, FiHeadphones
} from 'react-icons/fi';
import './Workflow.css';

const workflows = {
  app: {
    icon: FiSmartphone,
    steps: [
      { key: 'discovery', icon: FiMessageSquare, color: '#5cb0dd' },
      { key: 'wireframe', icon: FiLayout, color: '#8b5cf6' },
      { key: 'uiux', icon: FiFileText, color: '#ec4899' },
      { key: 'development', icon: FiCode, color: '#f59e0b' },
      { key: 'testing', icon: FiCheckCircle, color: '#10b981' },
      { key: 'publishing', icon: FiUploadCloud, color: '#ef4444' },
      { key: 'maintenance', icon: FiHeadphones, color: '#6366f1' },
    ]
  },
  web: {
    icon: FiGlobe,
    steps: [
      { key: 'planning', icon: FiMessageSquare, color: '#5cb0dd' },
      { key: 'design', icon: FiLayout, color: '#8b5cf6' },
      { key: 'frontend', icon: FiCode, color: '#f59e0b' },
      { key: 'backend', icon: FiDatabase, color: '#ec4899' },
      { key: 'qa', icon: FiShield, color: '#10b981' },
      { key: 'launch', icon: FiUploadCloud, color: '#ef4444' },
      { key: 'growth', icon: FiTrendingUp, color: '#6366f1' },
    ]
  }
};

const Workflow = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('app');

  const currentWorkflow = workflows[activeTab];

  return (
    <section id="workflow" className="section workflow-section">
      <div className="container">
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="section-tag">{t('workflow.sectionTag')}</span>
          <h2 className="section-title">{t('workflow.title')}</h2>
          <p className="section-subtitle">{t('workflow.subtitle')}</p>
        </motion.div>

        <motion.div
          className="workflow-tabs"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <button
            className={`workflow-tab ${activeTab === 'app' ? 'active' : ''}`}
            onClick={() => setActiveTab('app')}
          >
            <FiSmartphone />
            {t('workflow.tabs.app')}
          </button>
          <button
            className={`workflow-tab ${activeTab === 'web' ? 'active' : ''}`}
            onClick={() => setActiveTab('web')}
          >
            <FiGlobe />
            {t('workflow.tabs.web')}
          </button>
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            className="workflow-timeline"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
          >
            <div className="timeline-line"></div>
            {currentWorkflow.steps.map((step, idx) => {
              const Icon = step.icon;
              const isEven = idx % 2 === 0;
              return (
                <motion.div
                  key={step.key}
                  className={`timeline-item ${isEven ? 'left' : 'right'}`}
                  initial={{ opacity: 0, x: isEven ? -40 : 40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                >
                  <div className="timeline-dot" style={{ background: step.color }}>
                    <Icon size={18} />
                  </div>
                  <div className="timeline-card">
                    <div className="timeline-step-number" style={{ color: step.color }}>
                      {String(idx + 1).padStart(2, '0')}
                    </div>
                    <h4 className="timeline-card-title">
                      {t(`workflow.${activeTab}.${step.key}.title`)}
                    </h4>
                    <p className="timeline-card-desc">
                      {t(`workflow.${activeTab}.${step.key}.desc`)}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </AnimatePresence>

        <motion.div
          className="workflow-cta"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p>{t('workflow.cta')}</p>
          <a href="#contact" className="btn btn-primary">
            {t('workflow.ctaBtn')}
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Workflow;
